/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

export const containsFieldSpecificSearch = (obj: any, fieldPattern: string, valuePattern: string, objPath: string[] = []): boolean => {
  let fieldPatternLower = fieldPattern.toLowerCase();
  if ('source'.startsWith(fieldPatternLower)) {
    fieldPatternLower = 'document__slug';
  } else if (fieldPatternLower == 'ac') {
    fieldPatternLower = 'armor';
  }

  if (valuePattern === "!" || valuePattern === "-" || !valuePattern) {
    // If they haven't specified the value yet, let them continue typing, allow everything for this term
    return true;
  }

  if (valuePattern.startsWith("!") || valuePattern.startsWith("-")) {
    return !containsFieldSpecificSearch(obj, fieldPattern, valuePattern.substring(1), objPath);
  }

  // Check if any key at this level matches the field pattern
  for (const [key, value] of Object.entries(obj)) {
    const keyLower = key.toLowerCase();

    // If the key matches the field pattern, check if the value contains the value pattern
    // When we are looking for negative matches, we must match the field name exclusively,
    // because otherwise we may always fail or succeed in an indeterminant manner.
    // (ie. given a monster with "type:Dragon, subtype: Demon":
    //  A search on 'type:dragon' should return true, when it find keyLower='type', value='Dragon',
    //    but that means when we found keyLower='subtype' value='Demon', we didn't return 'false'.
    //  A search on 'type:!dragon' should return false, when it find keyLower='type', value='Dragon',
    //    but that means when we found keyLower='subtype' value='Demon', we didn't return 'true')
    if (keyLower.includes(fieldPatternLower) || keyLower === fieldPatternLower) {
      if (value && typeof value === 'string' && value.toLowerCase().includes(valuePattern.toLowerCase())) {
        return true;
      }
      // Also check if it's a number that matches
      if (typeof value === 'number' && value.toString().includes(valuePattern)) {
        return true;
      }
      // check if a child of the keyword contains the value:
      if (typeof value === 'object' && value !== null) {
        if (containsAllSearchWords(value, [valuePattern])) {
          return true;
        }
      }
    }

    // Recursively search nested objects
    if (typeof value === 'object' && value !== null) {
      if (containsFieldSpecificSearch(value, fieldPattern, valuePattern, [...objPath, key])) {
        return true;
      }
    }
  }
  return false;
};

export const containsAllSearchWords = (obj: any, searchWords: string[]): boolean => {
  const objValues = Object.values(obj);
  return searchWords.every(word =>
    objValues.some(value => {
      if (typeof value === 'object' && value !== null) {
        return containsAllSearchWords(value, [word]);  // Recursive call for nested objects
      }
      if (value && typeof value === 'string' && value.toLowerCase().includes(word.toLowerCase())) {
        return true;
      }
      return false;
    })
  );
};

export const containsAnySearchWords = (obj: any, searchWords: string[]): boolean => {
  const objValues = Object.values(obj);
  return searchWords.some(word =>
    objValues.some(value => {
      if (typeof value === 'object' && value !== null) {
        return containsAnySearchWords(value, [word]);  // Recursive call for nested objects
      }
      if (value && typeof value === 'string' && value.toLowerCase().includes(word.toLowerCase())) {
        return true;
      }
      return false;
    })
  );
};

export const containsSearchCriteria = (
  obj: any,
  positiveWords: string[],
  negativeWords: string[],
  fieldSpecificSearches: Array<{ field: string; value: string; negative: boolean }> = []
): boolean => {
  // Check regular search terms
  const regularSearchMatches = containsAllSearchWords(obj, positiveWords) && !containsAnySearchWords(obj, negativeWords);

  // Check field-specific searches
  for (const fieldSearch of fieldSpecificSearches) {
    const matches = containsFieldSpecificSearch(obj, fieldSearch.field, fieldSearch.value);

    if (fieldSearch.negative) {
      // Negative field-specific search - must NOT match
      if (matches) {
        return false;
      }
    } else {
      // Positive field-specific search - must match
      if (!matches) {
        return false;
      }
    }
  }

  return regularSearchMatches;
};

export const parseSearchString = (searchString: string) => {
  const regex = /-?"[^"]*"|-?'[^']*'|\S+/g;
  let match;
  const positive: string[] = [];
  const negative: string[] = [];
  const fieldSpecific: Array<{ field: string; value: string; negative: boolean }> = [];

  while ((match = regex.exec(searchString)) !== null) {
    const term = match[0];
    let cleanTerm = term;
    let isNegative = false;

    // Handle negative terms
    if ((term.startsWith('-"') && term.endsWith('"')) || (term.startsWith('-\'') && term.endsWith('\''))) {
      cleanTerm = term.slice(2, -1);
      isNegative = true;
    } else if ((term.startsWith('"') && term.endsWith('"')) || (term.startsWith('\'') && term.endsWith('\''))) {
      cleanTerm = term.slice(1, -1);
    } else if (term.startsWith('-')) {
      cleanTerm = term.slice(1);
      isNegative = true;
    }

    // Check if this is a field-specific search (contains a colon)
    if (cleanTerm.includes(':')) {
      const [fieldPattern, valuePattern] = cleanTerm.split(':', 2);
      if (fieldPattern && valuePattern) {
        fieldSpecific.push({
          field: fieldPattern.toLowerCase(),
          value: valuePattern.toLowerCase(),
          negative: isNegative
        });
      }
      continue;
    }

    // Regular search term
    if (isNegative) {
      negative.push(cleanTerm.toLowerCase());
    } else {
      positive.push(cleanTerm.toLowerCase());
    }
  }

  return { positive, negative, fieldSpecific };
};
